import { Locales } from '@griply/types';
import {
  applyScheduleEdit,
  formatTimeslotLabel,
  getAdjacentScheduleBounds,
  getHabitEndedDate,
  getHabitEndedLabel,
  getLastSchedule,
  getSmartDateLabel,
  resolveScheduleStartDate,
} from '@griply/utils';
import {
  addDays,
  endOfDay,
  format,
  isBefore,
  isSameDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Repeat } from 'shared/assets/icons';
import {
  DatepickerWithOptions,
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { HabitSchedulesMenu } from 'shared/components/ui/habit-schedules-menu';
import { IconButton } from 'shared/components/ui/icon-button';
import { RepeatForm } from 'shared/components/ui/repeat-rrule-form';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { DeleteHabitDialog } from 'shared/dialogs/delete-habit';
import { HabitScheduleDialog } from 'shared/dialogs/habit-schedule';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { InteractionType } from 'shared/types/data-type';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';
import { getScheduleLabel } from 'shared/utils/get-schedule-label';

import { useTaskDetailShortcut } from '../../hooks/use-task-detail-shortcut';
import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { useData } from '../../use-data';
import { MetaInteraction } from '../meta-interaction/meta-interaction';
import * as Styled from './meta-deadline.style';

const noop = () => {};

export type MetaDeadlineProps = {
  name: string;
  deadline?: Timestamp | null;
  type: TaskType;
  schedules?: HabitSchedule[] | null;
  dateFormat: DateFormatOptions;
  timeFormat: TimeFormatOptions;
  weekStartsOn: WeekDays;
  onChangeDeadline: (date?: Timestamp) => void;
  onChangeSchedule: (schedules: HabitSchedule[] | null) => void;
};

export const MetaDeadline: React.FC<MetaDeadlineProps> = ({
  name,
  deadline,
  schedules,
  type,
  onChangeDeadline,
  onChangeSchedule,
  weekStartsOn,
  dateFormat,
  timeFormat,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const locale = useI18nLocale();
  const buttonRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();
  const { createHabitTargetAllowed, createHabitAllowed, openPremiumDialog } =
    useData();
  const [showDeleteHabitDialog, setShowDeleteHabitDialog] = useState(false);
  const [schedulesMenuOpen, setSchedulesMenuOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<HabitSchedule | null>(
    null,
  );

  const shortcuts = useMemo(
    () => ({
      [DatePresets.Today]: getPlatformShortcut(['Shift', 'T']),
      [DatePresets.Tomorrow]: getPlatformShortcut(['Alt', 'Shift', 'T']),
      [DatePresets.NextWeek]: getPlatformShortcut(['Shift', 'W']),
    }),
    [],
  );

  const {
    menuOpen: repeatFormOpen,
    openMenu: openRepeatForm,
    closeMenu: closeRepeatForm,
  } = useTaskDetailSubmenu();

  const activeSchedule = useActiveSchedule(schedules, today);

  // No active schedule but schedules exist → every schedule has expired.
  const endedSchedule = !activeSchedule && !!schedules?.length;
  const lastSchedule = useMemo(() => getLastSchedule(schedules), [schedules]);

  const label = useMemo(() => {
    if (activeSchedule) {
      return getScheduleLabel(activeSchedule, t, true);
    }

    if (endedSchedule && lastSchedule) {
      return getScheduleLabel(lastSchedule, t, true);
    }

    if (!deadline) {
      return;
    }

    return getSmartDateLabel(deadline, today, t, { enableWeekdays: true });
  }, [activeSchedule, endedSchedule, lastSchedule, deadline, today, t]);

  const scheduleNotStarted =
    !!activeSchedule && isBefore(today, startOfDay(activeSchedule.startDate));

  const subLabel = useMemo(() => {
    if (!activeSchedule) {
      if (!endedSchedule) {
        return;
      }

      return (
        getHabitEndedLabel(getHabitEndedDate(schedules ?? [], today), t) ??
        undefined
      );
    }

    if (scheduleNotStarted) {
      return t('general.labels.starts-on', {
        date: format(activeSchedule.startDate, 'd MMM'),
      });
    }

    const completionCount = activeSchedule.completionTarget?.count ?? 1;
    const prefix =
      completionCount > 1
        ? `${t('general.labels.habit.repeat.value.target.per-day.label', {
            count: completionCount,
            label: t('general.labels.habit.repeat.value.target.default.label', {
              count: completionCount,
            }),
          })}, `
        : '';

    return `${prefix}${formatTimeslotLabel(activeSchedule.timeslot, {
      timeFormat,
      isNL: locale === Locales.NlNL,
      allDayLabel: t('general.labels.all-day'),
    })}`;
  }, [
    activeSchedule,
    endedSchedule,
    scheduleNotStarted,
    schedules,
    today,
    t,
    timeFormat,
    locale,
  ]);

  const shouldReplaceSchedule = useMemo(
    () =>
      activeSchedule?.startDate && isSameDay(activeSchedule?.startDate, today),
    [activeSchedule?.startDate, today],
  );

  const { minDate, maxDate } = useMemo(() => {
    if (!schedules || !editingSchedule) {
      return { minDate: undefined, maxDate: undefined };
    }

    return getAdjacentScheduleBounds(schedules, editingSchedule);
  }, [schedules, editingSchedule]);

  const onScheduleClick = () => {
    if (schedules?.length === 1) {
      setEditingSchedule(schedules[0]);
      return;
    }

    setSchedulesMenuOpen(true);
  };

  const closeMenus = () => {
    closeMenu();
    closeRepeatForm();
    setSchedulesMenuOpen(false);
    setEditingSchedule(null);
  };

  const _onChangeDeadline = (date?: Timestamp) => {
    onChangeDeadline(date);
    closeMenus();
  };

  const _onChangeSchedule = (schedule: HabitSchedule | null) => {
    if (!schedule) {
      onChangeSchedule(null);
      closeMenus();
      return;
    }

    const endedSchedules = schedules?.filter(({ endDate }) => !!endDate) ?? [];
    const updateSchedules = [];
    const activeSchedule = getActiveSchedule(schedules, today);
    const newSchedule = {
      ...schedule,
      timeslot: activeSchedule?.timeslot ?? null,
      startDate: deadline
        ? resolveScheduleStartDate(deadline, today)
        : schedule.startDate,
    };

    if (!shouldReplaceSchedule) {
      const endDate = endOfDay(today);
      !!activeSchedule &&
        updateSchedules.push({
          ...activeSchedule,
          endDate,
          plannedOccurrences: activeSchedule?.plannedOccurrences?.filter(
            ({ date }) => isBefore(date, startOfDay(endDate)),
          ),
        });
    }

    if (shouldReplaceSchedule && activeSchedule) {
      newSchedule.entries = activeSchedule.entries ?? [];
      newSchedule.completions = activeSchedule.completions ?? [];
      newSchedule.skips = activeSchedule.skips ?? null;
    }

    updateSchedules.push(newSchedule);

    onChangeSchedule([...endedSchedules, ...updateSchedules]);
    closeMenus();
  };

  const closeDeleteHabitDialog = () => setShowDeleteHabitDialog(false);

  const deleteSchedules = () => {
    onChangeSchedule(null);
    closeMenus();
    closeDeleteHabitDialog();
  };

  const onClear = () => {
    if (schedules?.length) {
      setShowDeleteHabitDialog(true);
      closeMenus();
      return;
    }

    onChangeDeadline();
    closeMenus();
  };

  // A task may never carry both a deadline and a schedule — once schedules
  // exist, the deadline shortcuts no-op and D routes to the schedule editor,
  // mirroring the pointer path.
  useTaskDetailShortcut(
    ['KeyD'],
    (e) => {
      if (!menuOpen && !repeatFormOpen) {
        e.preventDefault();
        e.stopPropagation();
        schedules?.length ? onScheduleClick() : openMenu();
      }
    },
    { ignoreInputFields: true, capture: true },
  );

  useTaskDetailShortcut(
    ['KeyT'],
    (e) => {
      if (menuOpen || schedules?.length) {
        return;
      }

      e.preventDefault();
      // If Alt is pressed, set tomorrow, otherwise set today
      if (e.altKey) {
        _onChangeDeadline(endOfDay(addDays(today, 1)));
        return;
      }
      _onChangeDeadline(endOfDay(today));
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  useTaskDetailShortcut(
    ['KeyW'],
    (e) => {
      if (menuOpen || !e.shiftKey || schedules?.length) {
        // Only trigger on Shift+W
        return;
      }

      e.preventDefault();
      _onChangeDeadline(
        endOfDay(
          startOfWeek(addDays(today, 7), {
            weekStartsOn: weekdaysNumberMap[weekStartsOn],
          }),
        ),
      );
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  useTaskDetailShortcut(
    ['KeyU'],
    (e) => {
      if (menuOpen || !e.shiftKey || schedules?.length) {
        // Only trigger on Shift+U
        return;
      }

      e.preventDefault();
      _onChangeDeadline(undefined);
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  const handleSchedulesMenuClose = () => {
    setSchedulesMenuOpen(false);
  };

  const handleScheduleSelect = (schedule: HabitSchedule) => {
    setEditingSchedule(schedule);
    setSchedulesMenuOpen(false);
  };

  const handleHabitScheduleCancel = () => {
    setEditingSchedule(null);
  };

  const handleHabitScheduleSubmit: NonNullable<
    React.ComponentProps<typeof HabitScheduleDialog>['onSubmit']
  > = (schedule, splitOptions) => {
    if (!editingSchedule) {
      return;
    }

    const updatedSchedules = applyScheduleEdit(
      schedules ?? [],
      editingSchedule,
      schedule,
      splitOptions,
    );

    onChangeSchedule(updatedSchedules);
    setEditingSchedule(null);
  };

  return (
    <>
      <Styled.Container>
        <MetaInteraction
          ref={buttonRef}
          icon={schedules ? Repeat : Calendar}
          placeholder={t('dialogs.task-detail.meta.deadline.placeholder')}
          label={label}
          subLabel={subLabel}
          subLabelInfo={scheduleNotStarted}
          subLabelError={endedSchedule}
          onClick={schedules ? onScheduleClick : openMenu}
          onClear={label ? onClear : undefined}
          selected={menuOpen || repeatFormOpen || schedulesMenuOpen}
        />

        {!schedules && (
          <Styled.IconButtonContainer>
            <IconButton icon={Repeat} onClick={openRepeatForm} />
          </Styled.IconButtonContainer>
        )}

        {repeatFormOpen && (
          <RepeatForm
            initialRrule={activeSchedule?.rrule?.format}
            scheduleStartDate={activeSchedule?.startDate}
            initialFrequency={activeSchedule?.frequency}
            weekStartsOn={weekStartsOn}
            type={type}
            onChange={_onChangeSchedule}
            onPremium={openPremiumDialog}
            isTargetPremium={!createHabitTargetAllowed}
            isCreateHabitPremium={!createHabitAllowed}
            onClose={closeMenus}
            referenceElement={buttonRef}
            position="bottom-start"
            interactionType={InteractionType.Task}
          />
        )}

        {menuOpen && (
          <DatepickerWithOptions
            referenceElement={buttonRef}
            value={deadline ?? undefined}
            onChange={_onChangeDeadline}
            dateFormat={dateFormat}
            weekStartsOn={weekStartsOn}
            icon={IconOptions.Calendar}
            onOutside={closeMenus}
            canRemoveValue
            shortcuts={shortcuts}
            clearShortcut={getPlatformShortcut(['Shift', 'U'])}
          />
        )}
      </Styled.Container>
      {schedulesMenuOpen && schedules && (
        <HabitSchedulesMenu
          schedules={schedules}
          referenceElement={buttonRef}
          onClose={handleSchedulesMenuClose}
          onSelect={handleScheduleSelect}
        />
      )}

      {!!editingSchedule && (
        <HabitScheduleDialog
          open={!!editingSchedule}
          initialValues={editingSchedule}
          isActive={
            !!activeSchedule &&
            activeSchedule.startDate.getTime() ===
              editingSchedule.startDate.getTime()
          }
          maxDate={maxDate}
          minDate={minDate}
          onCancel={handleHabitScheduleCancel}
          onSubmit={handleHabitScheduleSubmit}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          timeFormat={timeFormat}
          createHabitAllowed={createHabitAllowed}
          createHabitTargetAllowed={createHabitTargetAllowed}
          onPremium={openPremiumDialog}
        />
      )}

      {showDeleteHabitDialog && (
        <DeleteHabitDialog
          open={showDeleteHabitDialog}
          name={name}
          onClose={closeDeleteHabitDialog}
          onSubmit={deleteSchedules}
          onRetry={noop}
          isScheduleRemovalOnly
        />
      )}
    </>
  );
};
